import type {ReactNode} from 'react';
import clsx from 'clsx';
import Image from 'next/image';

import {ArrowLink} from '@/modules/finkn/components/arrow-link';
import {ContentBlockHeading} from '@/modules/foundation/components/content-block-heading';
import type {PortableTextOverrides} from '@/modules/foundation/sanity/portable-text';
import type {ContentBlockContainerProps} from '@/modules/page/components/content-blocks/block-container';
import {ContentBlockContainer} from '@/modules/page/components/content-blocks/block-container';

import type {GuidebookListAllFragment} from '../../sanity/guidebook-list-all/query';

interface Props extends ContentBlockContainerProps {
	heading?: string;
	description?: ReactNode;
	insurance?: {
		title: string;
		href: string;
	};
	bank?: {
		title: string;
		href: string;
	};
	collection?: {
		title: string;
		href: string;
	};
	anchorHeadings?: boolean;
	filter: GuidebookListAllFragment['filter'];
	insideToc?: boolean;
}

export const descriptionPortableTextOverrides: PortableTextOverrides = {
	p: 'text-xl',
};

export const GuidebookList = ({
	heading,
	description,
	insurance,
	bank,
	collection,
	anchorHeadings,
	options,
	filter,
	insideToc,
}: Props) => {
	const showAll = filter === 'all';

	return (
		<ContentBlockContainer
			className={clsx('text-primary-2')}
			options={options}
			insideToc={insideToc}
		>
			<div className={clsx('@container')}>
				<div className={clsx('grid', '@2xl:grid-cols-12', '@2xl:gap-10', 'relative')}>
					<div className="@2xl:col-span-4">
						<Image
							src="/images/guidebook-list-heading.svg"
							alt="Guidebook list heading"
							width={420}
							height={222}
							className={clsx('mb-10')}
						/>
						<ContentBlockHeading heading={heading} anchorHeading={anchorHeadings}>
							{description}
						</ContentBlockHeading>
					</div>

					<ul className="@2xl:col-span-7 @2xl:-col-end-1">
						{(filter === 'insurance-frontpage' || showAll) &&
							insurance?.title &&
							insurance?.href && (
								<ListItem
									key={insurance.title}
									title={insurance.title}
									href={insurance.href}
								/>
							)}
						{(filter === 'bank-frontpage' || showAll) && bank?.title && bank?.href && (
							<ListItem key={bank.title} title={bank.title} href={bank.href} />
						)}
						{(filter === 'collection-frontpage' || showAll) &&
							collection?.title &&
							collection.href && (
								<ListItem
									key={collection.title}
									title={collection.title}
									href={collection.href}
								/>
							)}
					</ul>
				</div>
			</div>
		</ContentBlockContainer>
	);
};

type ListItemProps = {
	href: string;
	title: string;
};

const ListItem = ({href, title}: ListItemProps) => {
	return (
		<li className={clsx('border-b', 'border-[#E2E8EE]', 'first-of-type:border-t')}>
			<ArrowLink className="py-5" href={href}>
				{title}
			</ArrowLink>
		</li>
	);
};
